// apps/web/src/features/agent/AgentVerdictBlock.tsx
//
// Renders the reconciliation verdict: outcome + confidence as the trust
// signal, plus a toggleable "show basis" panel that reveals the provenance
// trail (metric/SQL, period, filters, raw values). A `discrepancy` outcome
// is marked low-confidence regardless of the confidence field so the user is
// warned the sources disagreed.
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AgentVerdict, Provenance } from '@magimp/shared';
import { Button, Divider } from '../../ui/index.js';

export interface AgentVerdictBlockProps {
  verdict: AgentVerdict;
}

const OUTCOME_TONE: Record<AgentVerdict['outcome'], string> = {
  agreed: 'var(--status-success-fg)',
  reconciled: 'var(--status-info-fg)',
  discrepancy: 'var(--status-error-fg)',
};

export function AgentVerdictBlock({ verdict }: AgentVerdictBlockProps) {
  const { t } = useTranslation('agent');
  const [open, setOpen] = useState(false);
  const panelId = useId();

  // WHY: a discrepancy means sources disagreed and no definitive answer was
  // reached — surface it as low-confidence even if `confidence` says high.
  const lowConfidence = verdict.outcome === 'discrepancy' || verdict.confidence === 'low';

  return (
    <div
      data-testid="agent-verdict"
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        padding: 'var(--s-3)',
        background: 'var(--bg-1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: OUTCOME_TONE[verdict.outcome],
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>
          {t(`verdict.outcome.${verdict.outcome}`)}
        </span>
        <span
          data-testid="agent-verdict-confidence"
          style={{
            fontSize: 11,
            color: lowConfidence ? 'var(--status-error-fg)' : 'var(--fg-2)',
          }}
        >
          {t(lowConfidence ? 'verdict.confidence.low' : 'verdict.confidence.high')}
        </span>
        <Button
          kind="ghost"
          size="sm"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((v) => !v)}
          data-testid="agent-verdict-basis-toggle"
        >
          {t(open ? 'verdict.hideBasis' : 'verdict.showBasis')}
        </Button>
      </div>

      {open && (
        <div id={panelId} data-testid="agent-verdict-basis">
          <Divider style={{ margin: 'var(--s-1) 0' }} />
          <ProvenanceView provenance={verdict.basis} t={t} label={t('verdict.basisLabel')} />
          {verdict.candidates?.map((candidate, i) => (
            <ProvenanceView
              key={`candidate-${i}`}
              provenance={candidate}
              t={t}
              label={t('verdict.candidateLabel', { n: i + 1 })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type TFn = ReturnType<typeof useTranslation<'agent'>>['t'];

function ProvenanceView({
  provenance,
  t,
  label,
}: {
  provenance: Provenance;
  t: TFn;
  label: string;
}) {
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'var(--s-1) var(--s-3)',
        margin: 0,
        fontSize: 12,
      }}
    >
      <Row term={label} value={t(`verdict.source.${provenance.source}`)} />
      {provenance.taskLabel !== undefined && (
        <Row term={t('verdict.task')} value={provenance.taskLabel} />
      )}
      {provenance.taskId !== undefined && (
        <Row term={t('verdict.taskId')} value={provenance.taskId} mono />
      )}
      {provenance.metric !== undefined && (
        <Row term={t('verdict.metric')} value={provenance.metric} mono />
      )}
      {provenance.figureKeys !== undefined && provenance.figureKeys.length > 0 && (
        <Row term={t('verdict.figures')} value={provenance.figureKeys.join(', ')} mono />
      )}
      {provenance.noDataReason !== undefined && (
        <Row
          term={t('verdict.noDataReasonLabel')}
          value={t(`verdict.noDataReason.${provenance.noDataReason}`)}
        />
      )}
      {provenance.sql !== undefined && <Row term={t('verdict.sql')} value={provenance.sql} mono />}
      {provenance.period !== undefined && (
        <Row term={t('verdict.period')} value={provenance.period} />
      )}
      {provenance.filters !== undefined && (
        <Row term={t('verdict.filters')} value={JSON.stringify(provenance.filters)} mono />
      )}
      <Row term={t('verdict.values')} value={JSON.stringify(provenance.values)} mono />
    </dl>
  );
}

function Row({ term, value, mono }: { term: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt style={{ color: 'var(--fg-2)' }}>{term}</dt>
      <dd
        style={{
          margin: 0,
          color: 'var(--fg-1)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-ui)',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </dd>
    </>
  );
}
